import React, { useCallback, useRef } from "react";

interface TableHorizontalScrollTopProps {
  visible: boolean;
  trackWidth: number;
  thumbWidth: number;
  thumbLeft: number;
  maxScrollLeft: number;
  onScrollLeftChange: (scrollLeft: number) => void;
}

/** Always-visible horizontal scroll track synced with the table body. */
export default function TableHorizontalScrollTop({
  visible,
  trackWidth,
  thumbWidth,
  thumbLeft,
  maxScrollLeft,
  onScrollLeftChange,
}: TableHorizontalScrollTopProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{
    startX: number;
    startScrollLeft: number;
    scrollableTrack: number;
  } | null>(null);

  const clampScrollLeft = useCallback(
    (value: number) => Math.max(0, Math.min(maxScrollLeft, value)),
    [maxScrollLeft],
  );

  const scrollLeftFromThumbLeft = useCallback(
    (nextThumbLeft: number) => {
      const scrollableTrack = Math.max(trackWidth - thumbWidth, 0);
      if (scrollableTrack <= 0 || maxScrollLeft <= 0) {
        return 0;
      }
      return (nextThumbLeft / scrollableTrack) * maxScrollLeft;
    },
    [trackWidth, thumbWidth, maxScrollLeft],
  );

  const handleThumbMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (maxScrollLeft <= 0) {
      return;
    }

    const scrollableTrack = Math.max(trackWidth - thumbWidth, 0);
    if (scrollableTrack <= 0) {
      return;
    }

    dragStateRef.current = {
      startX: event.clientX,
      startScrollLeft: scrollLeftFromThumbLeft(thumbLeft),
      scrollableTrack,
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) {
        return;
      }

      const deltaX = moveEvent.clientX - dragState.startX;
      const deltaScroll =
        (deltaX / dragState.scrollableTrack) * maxScrollLeft;
      onScrollLeftChange(
        clampScrollLeft(dragState.startScrollLeft + deltaScroll),
      );
    };

    const handleMouseUp = () => {
      dragStateRef.current = null;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "grabbing";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleTrackMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (maxScrollLeft <= 0) {
      return;
    }
    if (event.target !== event.currentTarget) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const scrollableTrack = Math.max(trackWidth - thumbWidth, 0);
    const nextThumbLeft = Math.max(
      0,
      Math.min(scrollableTrack, clickX - thumbWidth / 2),
    );
    onScrollLeftChange(clampScrollLeft(scrollLeftFromThumbLeft(nextThumbLeft)));
  };

  if (!visible || trackWidth <= 0) {
    return null;
  }

  return (
    <div
      ref={trackRef}
      className="table-horizontal-scroll-top-track w-full shrink-0"
      style={{ width: trackWidth > 0 ? `${trackWidth}px` : "100%" }}
      onMouseDown={handleTrackMouseDown}
      role="presentation"
      aria-hidden
    >
      <div
        className="table-horizontal-scroll-top-thumb"
        style={{
          width: `${thumbWidth}px`,
          transform: `translateX(${thumbLeft}px)`,
        }}
        onMouseDown={handleThumbMouseDown}
      />
    </div>
  );
}
